import { Component, OnDestroy, OnInit } from '@angular/core';
import { StorageRouterService } from '../services/storage-router.service';
import { StoreStats, StoredEnvironment } from '../services/storage-backend';
import { UserItem } from '../services/storage-types';
import { Connection } from '../obj/Connection';
import { formatAgeAgo } from '../services/env-scan.util';
import { StorageWorkerProbeResult } from '../services/storage-worker.protocol';
import { Subscription } from 'rxjs';
import { FlowManagementService } from '../services/flow-management.service';
import { FlowStudioBrowserDataCacheService } from '../services/flow-studio-browser-data-cache.service';

@Component({
    selector: 'app-debug-store',
    templateUrl: './debug-store.component.html',
    standalone: false,
})
export class DebugStoreComponent implements OnInit, OnDestroy {
    public stats: StoreStats | null = null;
    public envs: StoredEnvironment[] = [];
    public configs: { key: string; value: any }[] = [];
    public users: UserItem[] = [];
    public userAliases: { email: string; user_key: string }[] = [];
    public connections: Connection[] = [];
    public mode = '';
    public workerProbe: StorageWorkerProbeResult | null = null;
    public loading = false;
    public nuking = false;
    public batchProbeLoading = false;
    public lastError: string | null = null;
    public lastNukeAt: string | null = null;
    public batchProbeEnvironment = '';
    public batchProbeAdmin = false;
    public batchProbeResult: string | null = null;
    public browserDataCacheProbeLoading = false;
    public browserDataCacheResetting = false;
    public browserDataCacheProductionResetting = false;
    public browserDataCacheStatusLoading = false;
    public browserDataCacheProbeResult: string | null = null;
    public browserDataCacheProductionStatusResult: string | null = null;
    public browserDataCacheProbeSourceMode: 'cached' | 'live' = 'cached';
    /** Currently selected table — drives which detail panel renders. */
    public selectedTable: string = 'environments';
    /** Tables that have a drill-down panel below the master list. */
    private readonly tablesWithDetail = new Set<string>([
        'environments', 'configs', 'users', 'user_aliases', 'connections',
    ]);
    private subscriptions = new Subscription();

    constructor(
        private storage: StorageRouterService,
        private flowManagement: FlowManagementService,
        private browserDataCache: FlowStudioBrowserDataCacheService,
    ) {}

    ngOnInit() {
        this.subscriptions.add(this.storage.storageMode$.subscribe((mode) => {
            this.mode = mode || '';
        }));
        this.subscriptions.add(this.storage.storageWorkerProbe$.subscribe((probe) => {
            this.workerProbe = probe;
        }));
        this.refresh();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    async refresh() {
        this.loading = true;
        this.lastError = null;
        try {
            this.mode = this.storage.getMode();
            const [stats, envs, configs, users, userAliases, connections] = await Promise.all([
                this.storage.getStoreStats(),
                this.storage.getEnvironments(),
                this.storage.getAllConfigs(),
                this.storage.getUsers(),
                this.storage.getAllUserAliases(),
                this.storage.getConnections(),
            ]);
            this.stats = stats;
            this.envs = envs.sort((a, b) => (a.displayName || a.name).localeCompare(b.displayName || b.name));
            if (!this.batchProbeEnvironment && this.envs.length) {
                this.batchProbeEnvironment = this.envs[0].name;
            }
            this.configs = configs;
            this.users = users.sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
            this.userAliases = userAliases;
            this.connections = (connections || []).sort((a, b) =>
                (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '')
            );
        } catch (err: any) {
            this.lastError = String(err?.message ?? err);
        } finally {
            this.loading = false;
        }
    }

    selectTable(name: string) {
        this.selectedTable = name;
    }

    hasDetail(name: string): boolean {
        return this.tablesWithDetail.has(name);
    }

    formatValue(v: any): string {
        if (v == null) return '';
        if (typeof v === 'string') return v;
        try { return JSON.stringify(v); } catch { return String(v); }
    }

    aliasCount(u: UserItem): number {
        return Array.isArray((u as any)?.emails) ? (u as any).emails.length : 0;
    }

    async nuke() {
        const ok = window.confirm(
            'Wipe ALL local cache (OPFS + Dexie)?\n\n' +
            'This deletes every cached flow, run, user, environment scan state, and config. ' +
            'On reload the app will re-scan from scratch. localStorage is left untouched.'
        );
        if (!ok) return;

        this.nuking = true;
        this.lastError = null;
        try {
            await this.storage.nukeStore();
            this.lastNukeAt = new Date().toISOString();
            await this.refresh();
        } catch (err: any) {
            this.lastError = String(err?.message ?? err);
        } finally {
            this.nuking = false;
        }
    }

    reloadPage() {
        window.location.reload();
    }

    async runBatchProbe() {
        const environment = this.batchProbeEnvironment || this.envs[0]?.name || '';
        if (!environment) {
            this.batchProbeResult = 'No cached environment available.';
            return;
        }

        this.batchProbeLoading = true;
        this.batchProbeResult = null;
        try {
            const summaries = await this.storage.getFlowSummaries(environment);
            const flow = (summaries || []).find((item: any) => !!item?.flowName);
            if (!flow) {
                this.batchProbeResult = `No cached flows found for ${environment}.`;
                return;
            }

            const started = Date.now();
            const [details, owners, runs] = await Promise.all([
                this.flowManagement.getFlowsBatch(environment, [flow.flowName], this.batchProbeAdmin),
                this.flowManagement.getFlowOwnersBatch(environment, [flow.flowName], this.batchProbeAdmin),
                this.flowManagement.getFlowRunsBatch(environment, [flow.flowName], this.batchProbeAdmin),
            ]);
            const detailOk = details.has(flow.flowName);
            const ownerCount = owners.get(flow.flowName)?.length ?? 0;
            const runCount = runs.get(flow.flowName)?.length ?? 0;
            this.batchProbeResult = [
                `flow=${flow.displayName || flow.flowName}`,
                `detail=${detailOk ? 'ok' : 'missing'}`,
                `owners=${ownerCount}`,
                `runs=${runCount}`,
                `mode=${this.mode || this.storage.getMode()}`,
                `duration=${Date.now() - started}ms`,
            ].join(' / ');
        } catch (err: any) {
            this.batchProbeResult = `Batch probe failed: ${String(err?.message ?? err)}`;
        } finally {
            this.batchProbeLoading = false;
        }
    }

    async runBrowserDataCacheProbe() {
        const environment = this.batchProbeEnvironment || this.envs[0]?.name || '';
        if (!environment) {
            this.browserDataCacheProbeResult = 'No cached environment available.';
            return;
        }

        this.browserDataCacheProbeLoading = true;
        this.browserDataCacheProbeResult = null;
        try {
            const started = Date.now();
            const result = await this.browserDataCache.runFlowSummaryProbe(
                environment,
                this.browserDataCacheProbeSourceMode,
                this.browserDataCacheProbeSourceMode === 'live'
                    ? async (cursor) => {
                        const page = await this.flowManagement.getFlowListPageForBrowserDataCacheProbe(environment, cursor);
                        return { items: page.items, nextCursor: page.nextCursor || null, total: page.total };
                    }
                    : undefined,
            );
            const sample = result.sample
                .map((flow) => flow.displayName || flow.flowName)
                .filter(Boolean)
                .slice(0, 3)
                .join(', ');
            this.browserDataCacheProbeResult = [
                `runtime=${result.runtime}`,
                `selected=${result.selectedRuntimeId}`,
                result.runtimeProbeSummary ? `probes=${result.runtimeProbeSummary}` : '',
                `source=${result.sourceMode}`,
                `persistent=${result.persistent ? 'yes' : 'no'}`,
                `sql=${result.sql ? 'yes' : 'no'}`,
                `written=${result.rowsWritten}`,
                `returned=${result.rowsReturned}`,
                sample ? `sample=${sample}` : '',
                `duration=${Date.now() - started}ms`,
            ].filter(Boolean).join(' / ');
        } catch (err: any) {
            this.browserDataCacheProbeResult = `Browser data cache probe failed: ${String(err?.message ?? err)}`;
        } finally {
            this.browserDataCacheProbeLoading = false;
        }
    }

    async resetBrowserDataCacheDebugDb() {
        this.browserDataCacheResetting = true;
        this.browserDataCacheProbeResult = null;
        try {
            await this.browserDataCache.resetDebugCache();
            this.browserDataCacheProbeResult = 'Package debug DB reset.';
        } catch (err: any) {
            this.browserDataCacheProbeResult = `Package debug DB reset failed: ${String(err?.message ?? err)}`;
        } finally {
            this.browserDataCacheResetting = false;
        }
    }

    async resetBrowserDataCacheProductionDb() {
        const ok = window.confirm(
            'Reset the browser-data-cache production mirror?\n\n' +
            'This deletes only flowstudio-flows-v1.sqlite and its probe file. ' +
            'Existing FlowStudio app storage is left untouched.'
        );
        if (!ok) return;

        this.browserDataCacheProductionResetting = true;
        this.browserDataCacheProductionStatusResult = null;
        try {
            await this.browserDataCache.resetProductionCache();
            this.browserDataCacheProductionStatusResult = 'Package production mirror DB reset.';
        } catch (err: any) {
            this.browserDataCacheProductionStatusResult = `Package production mirror DB reset failed: ${String(err?.message ?? err)}`;
        } finally {
            this.browserDataCacheProductionResetting = false;
        }
    }

    async checkBrowserDataCacheProductionStatus() {
        const environment = this.batchProbeEnvironment || this.envs[0]?.name || '';
        this.browserDataCacheStatusLoading = true;
        this.browserDataCacheProductionStatusResult = null;
        try {
            const status = await this.browserDataCache.getProductionCacheStatus(
                environment || undefined,
                this.batchProbeAdmin ? 'admin' : 'user',
            );
            this.browserDataCacheProductionStatusResult = [
                `runtime=${status.runtime}`,
                `selected=${status.selectedRuntimeId}`,
                status.runtimeProbeSummary ? `probes=${status.runtimeProbeSummary}` : '',
                `persistent=${status.persistent ? 'yes' : 'no'}`,
                `sql=${status.sql ? 'yes' : 'no'}`,
                `fallback=${status.fallback ? 'yes' : 'no'}`,
                status.environment ? `environment=${status.environment}` : '',
                status.scope ? `scope=${status.scope}` : '',
                status.rowsReturned != null ? `rows=${status.rowsReturned}` : '',
                status.error ? `error=${status.error}` : '',
            ].filter(Boolean).join(' / ');
        } catch (err: any) {
            this.browserDataCacheProductionStatusResult = `Production cache status failed: ${String(err?.message ?? err)}`;
        } finally {
            this.browserDataCacheStatusLoading = false;
        }
    }

    formatBytes(n: number | null | undefined): string {
        if (n == null) return '—';
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
        return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    }

    formatAgeFromMs(ms: number | null | undefined): string {
        if (ms == null) return 'never';
        return formatAgeAgo(Date.now() - ms);
    }

    formatTimestamp(ms: number | null | undefined): string {
        if (ms == null) return '';
        try { return new Date(ms).toLocaleString(); } catch { return ''; }
    }

    formatDuration(ms: number | null | undefined): string {
        if (ms == null) return '—';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    }

    get tableRows(): { name: string; count: number }[] {
        const t = this.stats?.tables ?? {};
        return Object.keys(t).map((k) => ({ name: k, count: t[k] }));
    }
}
